import * as React from 'react';

const { createContext, useState, useCallback, useContext } = React;

interface UpgradeModalContent {
    title: string;
    description: string;
    ctaText?: string;
}

interface UIContextState {
    toast: string;
    showToast: (message: string) => void;
    isUpgradeModalOpen: boolean;
    upgradeModalContent: UpgradeModalContent | null;
    openUpgradeModal: (content: UpgradeModalContent) => void;
    closeUpgradeModal: () => void;
}

const UIContext = createContext<UIContextState | null>(null);

export const useUI = () => {
    const context = useContext(UIContext);
    if (!context) throw new Error('useUI must be used within a UIProvider');
    return context;
};

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toast, setToast] = useState('');
    
    // Upgrade Modal State
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [upgradeModalContent, setUpgradeModalContent] = useState<UpgradeModalContent | null>(null);

    const showToast = useCallback((message: string) => {
        setToast(message);
        setTimeout(() => setToast(''), 3000);
    }, []);

    const openUpgradeModal = useCallback((content: UpgradeModalContent) => {
        setUpgradeModalContent(content);
        setIsUpgradeModalOpen(true);
    }, []);

    const closeUpgradeModal = useCallback(() => {
        setIsUpgradeModalOpen(false);
        setUpgradeModalContent(null);
    }, []);

    const value = { 
        toast, 
        showToast, 
        isUpgradeModalOpen, 
        upgradeModalContent, 
        openUpgradeModal, 
        closeUpgradeModal 
    };

    return (
        <UIContext.Provider value={value}>
            {children}
        </UIContext.Provider>
    );
};